import { charToken, isPositionalToken } from "@/domain/tokens";
import { flattenRawTable, parseRaw, rewriteValues, valueAlts } from "@/games/sc2/parser/index";
import { codeToChar, qwerty } from "@/hardware/keyboard-layouts/index";
import { alignVariants, charKeyCode } from "./align-variants";

/**
 * TheCore's Left hand is a positional reflection of its Right: the author scripts it
 * from the one hand-authored Right file. We recover that reflection once by aligning the vendored
 * US-Qwerty Right and Left files Command by Command - the same `alignVariants` alignment as the
 * derive-layout recoveries, but yielding a physical-key `code -> code` table instead of a
 * `code -> char` one, so thereafter a TheCore edition needs only its Right seed.
 */
export interface HandMirror {
  /** Right physical-key slot to left physical-key slot. A slot is a KeyboardEvent.code for char
   * keys (resolved through qwerty) or the raw .SC2Hotkeys named token (Enter, Tab, F-keys) verbatim. */
  mirror: Record<string, string>;
  /** Slots the alignment voted more than one target for (ranked, highest first); each needs triage. */
  mismatches: Record<string, string[]>;
}

/**
 * Resolve a key token to a stable physical slot: a KeyboardEvent.code for a char-emitting key
 * (through qwerty, the canonical layout both files serialize), or the raw named token otherwise.
 * Named keys (Enter, Tab, CapsLock, F-keys) are their own slot, so the mirror spans the pinky
 * column a char-only alignment would drop.
 */
function slot(key: string): string | null {
  return isPositionalToken(key) ? key : charKeyCode(key);
}

/**
 * Recover the Right->Left positional reflection from a hand-paired seed. Both files serialize the
 * same Commands, so for each paired binding the right key names the physical slot
 * the right hand uses and the left key names the slot the left hand uses. Comma alternatives pair
 * by index; a modifier mismatch in an alternative drops that alternative's vote (a hand tweak that
 * also moved a modifier is not a positional remap). The majority slot wins; a split vote is reported.
 */
export function learnHandMirror(rightText: string, leftText: string): HandMirror {
  // Both sides resolve a key to its physical slot; alignVariants votes the R->L reflection per slot.
  // A per-hand modifier tweak on one alternative is not a reason to drop a clean reflection on
  // another, so a modifier mismatch drops just that alternative (unlike the layout recoveries).
  const { map, conflicts } = alignVariants(rightText, leftText, slot, slot, {
    onMismatch: "skip-alternative",
  });
  const mirror = { ...map };
  // A reflection is its own inverse, so learning `a -> b` implies `b -> a`. The right seed only binds
  // half a key pair (e.g. v6 binds O but not E), leaving the inverse unlearned; that hole bites when
  // the mirror is applied to another edition that DOES bind it. Fill each unlearned inverse from its
  // pair (first-seen wins if a target was voted from two sources, which a clean reflection never does).
  for (const [a, b] of Object.entries(map)) {
    if (!(b in mirror)) mirror[b] = a;
  }
  return { mirror, mismatches: conflicts };
}

/** Serialize a mirror slot back to its US-Qwerty .SC2Hotkeys token (both hands are US-Qwerty). A
 * positional slot is already its token; a char-key code reverses through qwerty to the char's token. */
function serializeSlot(toSlot: string): string | null {
  if (isPositionalToken(toSlot)) return toSlot;
  const ch = codeToChar(qwerty, toSlot);
  return ch == null ? null : charToken(ch);
}

/** Reflect one key token to the other hand's token; unknown or unmapped keys pass through. */
function mirrorKey(token: string, mirror: Record<string, string>): string {
  const from = slot(token);
  if (from == null) return token;
  const to = mirror[from];
  if (to == null) return token;
  return serializeSlot(to) ?? token;
}

/** F-key tokens (F1-F24): positional, yet TheCore's own files reflect them per-command. */
const FKEY = /^F\d{1,2}$/;

/**
 * Reflectable keys a Right seed binds that the mirror has no entry for. A future edition binding a
 * key v6 never did would otherwise pass through unmirrored (the E/R-hole failure), so a non-empty
 * result must be reviewed before trusting the generated Left - a cheap sanity check.
 * Positional keys (Enter, mouse, numpad) legitimately do not reflect and are exempt - EXCEPT
 * F-keys: TheCore itself reflects those (the v6 pair learns F1<->F8, F5<->F10 and identity F3/F9),
 * so an F-key the mirror has not learned is a real hole, not noise. Every F-key the shipped
 * generation seeds bind is in the learned mirror, so no exception list is needed today.
 */
export function mirrorGaps(rightText: string, mirror: Record<string, string>): string[] {
  const gaps = new Set<string>();
  for (const value of Object.values(flattenRawTable(parseRaw(rightText)))) {
    for (const { key } of valueAlts(value)) {
      if (!key || (isPositionalToken(key) && !FKEY.test(key))) continue;
      const from = slot(key);
      if (from != null && !(from in mirror)) gaps.add(key);
    }
  }
  return [...gaps];
}

/**
 * Generate the Left-hand .SC2Hotkeys text from a Right-hand seed by applying a learned mirror - the
 * hand-axis sibling of convert's locale axis. Only each binding's key token moves;
 * modifiers, comma alternatives, ordering, comments and the [Settings] section are preserved exactly
 * by the shared rewriteValues walker.
 */
export function applyHandMirror(rightText: string, mirror: Record<string, string>): string {
  return rewriteValues(rightText, (key) => mirrorKey(key, mirror));
}
